import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { BroadcastService } from '../../services/broadcast.service';
import { Organization } from '../models/organization.model';
import { RepresentativeAdd } from '../models/representative-add.model';
import { ToroAccountDetailed } from '../models/toro-account-detailed.model';
import { ToroAccountSimple } from '../models/toro-account-simple.model';
import { User } from '../models/user.model';
import { AccountApiService } from '../services/account-api.service';
import { LocalCacheManager } from './local-cache.manager';
import { LoginAndUserManagerService } from './login-and-user-manager.service';
import { NotificationManagerService } from './notification-manager.service';
import { Store } from '@ngrx/store';
import { AccountState } from './../../accounts/store/account/account.entity';
import * as accountSelectors from './../../accounts/store/account/account.selectors';
import { accountActionTypes } from './../../accounts/store/account/account.actions';
import { Constants } from '../../core/constants';
@Injectable()
export class AccountManagerService implements OnDestroy {
    public accountDetailsUpdated = new EventEmitter();

    // Private member variables
    private _broadcastSubscription: Subscription;
    private _currentAccount: ToroAccountDetailed;
    private _detailedAccountsList: ToroAccountDetailed[] = [];
    private _representativesDict: { [accountId: number]: User[]; } = {};

    // C'tor
    constructor(private accountApiService: AccountApiService,
        private userManager: LoginAndUserManagerService,
        private localCacheManager: LocalCacheManager,
        private broadcastService: BroadcastService,
        private accountStore: Store<AccountState>,
        private notificationManager: NotificationManagerService) {

        this.broadcastService.userSignedOut.subscribe(() => {
            this.clearCache();
        });
    }

    ngOnDestroy() {
        this._broadcastSubscription.unsubscribe();
    }

    public clearCache() {
        this._detailedAccountsList = [];
        this.currentAccount = null;
        this.localCacheManager.detailedAccounts = null;
        this.localCacheManager.currentUser = null;
        this._representativesDict = {};
    }

    // Public Properties ===============================================================================================

    get accountsAdministered(): ToroAccountSimple[] {
        // let tempAccountsAdministered =  this.userManager.currentUser.AccountsAdministered;
        if (this.userManager.currentUser && this.userManager.currentUser !== null) {
            return this.userManager.currentUser.AccountsAdministered.slice().sort(this.accountsComparer);
        }
        else {
            return null;
        }
        return this.userManager.currentUser.AccountsAdministered.slice().sort(this.accountsComparer);
    }

    get accountsRepresenting(): ToroAccountSimple[] {
        if (this.userManager.currentUser && this.userManager.currentUser !== null) {
            return this.userManager.currentUser.AccountsRepresenting.slice().sort(this.accountsComparer);
        }
        else {
            return null;
        }

    }

    get currentAccount(): ToroAccountDetailed {
        if (this._currentAccount === undefined) {
            this.accountStore.select(accountSelectors.currentSelectedAccountDetail).subscribe(acc => {
                this._currentAccount = acc;
                return this._currentAccount;
            });
        }
        else {
            return this._currentAccount;
        }


    }

    set currentAccount(value: ToroAccountDetailed) {
        this._currentAccount = value;
        this.localCacheManager.selectedAccountId = value ? value.Id : null;

        if (this._currentAccount) {
            //this.notificationManager.restartPolling(this._currentAccount.Id, Constants.longPollInterval);
        }
    }

    // Public Methods ==================================================================================================

    public getAccountsList(cached: boolean = true, detailed: boolean = true): Observable<any[]> {

        const allAccounts = this.accountsAdministered && this.accountsAdministered !== null ? this.accountsAdministered.concat(this.accountsRepresenting) : null;

        // Return list of ToroAccountSimple objects
        if (!detailed) {
            return of(allAccounts);
        }

        // Return cached ToroAccountDetail[] if user requested and it exists.
        if ((cached && (this._detailedAccountsList && this._detailedAccountsList !== null)) && this._detailedAccountsList.length > 0) {
            return of(this._detailedAccountsList);
        }

        if (cached && this.localCacheManager.detailedAccounts) {
            return of(this.localCacheManager.detailedAccounts);
        }

        // Fetch fresh data
        const promises: Observable<number>[] = [];
        if (allAccounts && allAccounts !== null) {
            for (const account of allAccounts) {
                const p = this.getAccountDetails(account)
                    .pipe(
                        map(detailAccount => this._detailedAccountsList.push(detailAccount))
                    );

                promises.push(p);
            }
        }
        // for (const account of allAccounts) {
        //     const p = this.getAccountDetails(account)
        //         .pipe(
        //             map(detailAccount => this._detailedAccountsList.push(detailAccount))
        //         );

        //     promises.push(p);
        // }

        return forkJoin(promises)
            .pipe(
                map(() => {
                    this._detailedAccountsList.sort(this.accountsComparer);
                    this.localCacheManager.detailedAccounts = this._detailedAccountsList;
                    return this._detailedAccountsList;
                })
            );
    }

    public getAccount(accountId: number): ToroAccountDetailed {
        let accountDetailed;

        if ((this._detailedAccountsList && this._detailedAccountsList !== null) && this._detailedAccountsList.length > 0) {
            accountDetailed = this._detailedAccountsList.find(acc => acc.Id === accountId);

            if (accountDetailed) {
                return accountDetailed;
            }
        }

        if (this.localCacheManager.detailedAccounts) {
            this._detailedAccountsList = this.localCacheManager.detailedAccounts;
            return this._detailedAccountsList.find(acc => acc.Id === accountId) || null;
        }

        return null;
    }

    public getAccountDetailsById(accountId: number): Observable<ToroAccountDetailed> {
        return this.accountApiService.getAccountDetailsById(accountId);
    }

    public getAccountDetails(account: ToroAccountSimple): Observable<ToroAccountDetailed> {
        return this.accountApiService.getAccountDetails(account.Id, account.AccountType);
    }

    public updateAccountAddress(accountId: number, organization: Organization): Observable<{ AccountId: number, Organization: Organization, Success: boolean, Error, string }> {
        return this.accountApiService.updateAccountAddress(accountId, organization)
            .pipe(
                tap(() => {
                    this._detailedAccountsList.find(acc => acc.Id === accountId).Organization = organization;
                    this.localCacheManager.detailedAccounts = this._detailedAccountsList;

                    const accountsAdministered = this.userManager.currentUser.AccountsAdministered;
                    accountsAdministered.find(acc => acc.Id === accountId).Name = organization.Name;
                })
            );
    }

    public updateAccountDetails(accountId: number) {
        this.getAccountDetailsById(accountId)
            .subscribe((accountDetailed) => {
                this.accountStore.dispatch(accountActionTypes.LoadAccounts({ useCache: false, detailed: true }));
                if ((this._detailedAccountsList && this._detailedAccountsList !== null) && this._detailedAccountsList.length > 0) {
                    this._detailedAccountsList.forEach(ac => {
                        if (ac.Id === accountId) {
                            ac = accountDetailed;
                        }
                    })
                }
                else {
                    this._detailedAccountsList.push(accountDetailed);
                }

                this.localCacheManager.detailedAccounts.forEach(x => {
                    if (x.Id === accountId) {
                        x = accountDetailed;
                    }
                })
                // this.localCacheManager.detailedAccounts[accountId] = accountDetailed;
                this.accountDetailsUpdated.emit();
            });
    }

    public isAdministeredAccount(accountId: number): boolean {
        return this.accountsAdministered[0] ? accountId === this.accountsAdministered[0].Id : false;
    }

    public deleteAccount(accountId: number): Observable<any> {
        return this.accountApiService.deleteAccount(accountId);
    }

    public getRepresentatives(accountId: number, organizationId: number, cached: boolean = true): Observable<User[]> {
        if (cached && this._representativesDict[accountId]) {
            return of(this._representativesDict[accountId]);
        }

        return this.accountApiService.getRepresentatives(accountId, organizationId)
            .pipe(
                tap((representatives: User[]) => {
                    this._representativesDict[accountId] = representatives;
                })
            );
    }

    public addRepresentative(accountId: number, representativeToAdd: RepresentativeAdd): Observable<any> {
        if (representativeToAdd.IsExisting) {
            return this.accountApiService.addExistingUserAsRepresentative(accountId, representativeToAdd)
                .pipe(
                    tap((representatives: User[]) => {
                        this._representativesDict[accountId] = representatives;
                    })
                );
        }

        return this.accountApiService.addNewUserAsRepresentative(accountId, representativeToAdd)
            .pipe(
                tap((representatives: User[]) => {
                    this._representativesDict[accountId] = representatives;
                })
            );

    }

    public removeRepresentative(userId: number, organizationId: number, accountId: number): Observable<any> {
        return this.accountApiService.removeRepresentative(userId, organizationId)
            .pipe(
                tap(() => {
                    const representativeIndexToRemove = this._representativesDict[accountId].findIndex(user => user.Id === userId);
                    this._representativesDict[accountId].splice(representativeIndexToRemove, 1);
                })
            );
    }

    public removeSelfAsRepresentative(organizationId: number, accountId: number): Observable<any> {
        return this.accountApiService.removeSelfAsRepresentative(organizationId)
            .pipe(
                tap(() => {
                    const detailedAccountIndexToRemove = this._detailedAccountsList.findIndex(account => account.Id === accountId);
                    this._detailedAccountsList.splice(detailedAccountIndexToRemove, 1);
                    this.localCacheManager.detailedAccounts = this._detailedAccountsList;

                    const representingAccountIndexToRemove = this.accountsRepresenting.findIndex(account => account.Id === accountId);
                    this.accountsRepresenting.splice(representingAccountIndexToRemove, 1);
                })
            );
    }

    // Helper Methods ==================================================================================================

    private accountsComparer(a1: ToroAccountDetailed, a2: ToroAccountDetailed): number {

        if (a1.AccountType !== a2.AccountType) {
            if (a1.AccountType > a2.AccountType) {
                return 1;
            }
            if (a1.AccountType < a2.AccountType) {
                return -1;
            }
        }

        if (a1.Id > a2.Id) {
            return 1;
        }
        if (a1.Id < a2.Id) {
            return -1;
        }
        return 0;
    }
}
